import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/UI/dialog';
import Button from '@/components/UI/Button';
import Badge from '@/components/UI/Badge';
import { ordersAPI, positionsAPI, strategiesAPI } from '@/services/api';
import { Spinner } from '@/components/motion';
import formatMoney from '@/lib/formatMoney';

/* ── Helpers matching OrderBook conventions ── */

const ORDER_TYPE_BADGES = {
  limit: { variant: 'info', label: 'LIMIT' },
  market: { variant: 'primary', label: 'MARKET' },
  stop: { variant: 'warning', label: 'STOP' },
  stop_limit: { variant: 'warning', label: 'STOP LMT' },
};

const getOrderTypeBadge = (type) => {
  const cfg = ORDER_TYPE_BADGES[type] || { variant: 'neutral', label: type?.toUpperCase() };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

const MiniSpinner = () => (
  <div className="animate-spin rounded-full h-3.5 w-3.5 border-[1.5px] border-yellow-400 border-t-transparent flex-shrink-0" />
);

const formatPrice = (v) =>
  v != null ? formatMoney(v) : null;

const formatCurrency = (v) => formatMoney(v);

/** Normalize symbol for comparison (strip slashes, uppercase) */
const normalizeSymbol = (s) => (s || '').replace(/\//g, '').toUpperCase();

/* ── Component ── */

function DeleteStrategyModal({ strategy, open, onClose, onConfirm, mode = 'archive' }) {
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [liquidate, setLiquidate] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  // Running strategies that share a ticker with this strategy's open positions
  const [conflictingStrategies, setConflictingStrategies] = useState([]);

  useEffect(() => {
    if (!open || !strategy) return;

    setLoading(true);
    setDeleting(false);
    setLiquidate(false);
    setConfirmText('');
    setConflictingStrategies([]);

    Promise.all([
      ordersAPI.getByStrategy(strategy.id, 'open').catch(() => ({ data: [] })),
      positionsAPI.getByStrategy(strategy.id).catch(() => ({ data: [] })),
      strategiesAPI.getAll({ status: 'running' }).catch(() => ({ data: [] })),
    ]).then(([ordersRes, positionsRes, strategiesRes]) => {
      const ords = ordersRes.data || [];
      const pos = positionsRes.data || [];
      const allStrategies = strategiesRes.data || strategiesRes || [];

      setOrders(ords);
      setPositions(pos);

      // Find running strategies (not this one) that have positions on same tickers
      if (pos.length > 0) {
        const mySymbols = new Set(pos.map((p) => normalizeSymbol(p.symbol)));
        const conflicts = allStrategies.filter(
          (s) =>
            s.id !== strategy.id &&
            s.status === 'running' &&
            s.parameters?.symbol &&
            mySymbols.has(normalizeSymbol(s.parameters.symbol))
        );
        setConflictingStrategies(conflicts);
      }

      setLoading(false);
    });
  }, [open, strategy]);

  const hasConflict = conflictingStrategies.length > 0;
  const needsConfirmation = liquidate && hasConflict;
  const confirmValid = !needsConfirmation || confirmText.toLowerCase() === 'confirm';

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm({ liquidate });
    } finally {
      setDeleting(false);
    }
  };

  if (!strategy) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !deleting && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'archive' ? 'Archive' : 'Delete'} Strategy: &ldquo;{strategy.name}&rdquo;</DialogTitle>
          <DialogDescription>
            Review the impact before {mode === 'archive' ? 'archiving' : 'deleting'} this strategy.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-5">
            {/* ── Unfilled Orders ── */}
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                Unfilled Orders ({orders.length})
              </h4>
              {orders.length > 0 ? (
                <>
                  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-[10px] text-muted-foreground/60 font-medium px-3 py-1.5 border-b border-white/5">
                      <div>Side / Type</div>
                      <div className="text-right">Shares</div>
                      <div className="text-right">Price</div>
                      <div className="text-right">Symbol</div>
                    </div>

                    {/* Order rows */}
                    <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                      {orders.map((o) => {
                        const qty = parseFloat(o.quantity);
                        const price = o.limit_price ?? o.stop_price ?? o.price ?? null;

                        return (
                          <div key={o.id} className="px-3 py-2">
                            {deleting ? (
                              /* Canceling state — matches OrderBook MiniSpinner row */
                              <div className="flex items-center gap-2.5">
                                <MiniSpinner />
                                <span className="text-xs text-yellow-400 animate-pulse">
                                  Canceling {o.side?.toUpperCase()} {qty} {o.symbol}
                                  {formatPrice(price) ? ` @ ${formatPrice(price)}` : ''}...
                                </span>
                              </div>
                            ) : (
                              /* Normal detail row */
                              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-xs font-bold ${o.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                                    {o.side?.toUpperCase()}
                                  </span>
                                  {getOrderTypeBadge(o.order_type || o.type)}
                                </div>
                                <div className="text-xs text-muted-foreground text-right">{qty}</div>
                                <div className="text-xs text-muted-foreground text-right">
                                  {formatPrice(price) || 'MKT'}
                                </div>
                                <div className="text-xs font-mono font-semibold text-foreground text-right">
                                  {o.symbol}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground/60 mt-1.5">
                    {deleting
                      ? 'Canceling orders at broker...'
                      : `These orders will be automatically canceled before ${mode === 'archive' ? 'archiving' : 'deleting'}.`}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground/60">No unfilled orders.</p>
              )}
            </div>

            {/* ── Open Positions ── */}
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                Open Positions ({positions.length})
              </h4>
              {positions.length > 0 ? (
                <>
                  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                    <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                      {positions.map((p) => {
                        const qty = parseFloat(p.quantity);
                        const entry = parseFloat(p.entry_price);
                        const current = parseFloat(p.current_price || p.entry_price);
                        const pnl = (current - entry) * qty;
                        const pnlPct = entry > 0 ? ((current - entry) / entry) * 100 : 0;

                        return (
                          <div key={p.id} className="px-3 py-2 flex items-center justify-between">
                            <div>
                              <span className="font-semibold text-foreground text-sm">{p.symbol}</span>
                              <span className="text-muted-foreground text-sm ml-2">
                                {qty} shares @ {formatCurrency(entry)}
                              </span>
                            </div>
                            <span className={`text-sm font-medium ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatMoney(pnl, { sign: true })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Liquidation toggle ── */}
                  <label className="flex items-center gap-2.5 mt-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={liquidate}
                      onChange={(e) => {
                        setLiquidate(e.target.checked);
                        if (!e.target.checked) setConfirmText('');
                      }}
                      className="h-4 w-4 rounded border-white/20 bg-white/5 text-amber-500 focus:ring-amber-500/30"
                    />
                    <span className="text-sm text-foreground">Liquidate positions at market</span>
                  </label>

                  {!liquidate && (
                    <p className="text-xs text-muted-foreground/60 mt-1.5">
                      Positions will remain open and can be managed from other strategies or manually.
                    </p>
                  )}

                  {/* ── Shared ticker warning + confirm input ── */}
                  {liquidate && hasConflict && (
                    <div className="mt-3 space-y-2">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-red-400 font-medium mb-1">
                          Active strategy conflict detected
                        </p>
                        <p className="text-xs text-red-400/80">
                          {conflictingStrategies.length === 1
                            ? `"${conflictingStrategies[0].name}" is running with a position on the same ticker.`
                            : `${conflictingStrategies.length} running strategies share a ticker with these positions:`}
                          {conflictingStrategies.length > 1 && (
                            <span className="block mt-1">
                              {conflictingStrategies.map((s) => `"${s.name}"`).join(', ')}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-red-400/80 mt-1.5">
                          Liquidating may close positions that the running {conflictingStrategies.length === 1 ? 'strategy relies' : 'strategies rely'} on.
                          Type <span className="font-mono font-bold text-red-300">confirm</span> to proceed.
                        </p>
                      </div>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder='Type "confirm" to liquidate'
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-red-500/50"
                        autoComplete="off"
                      />
                    </div>
                  )}

                  {liquidate && !hasConflict && (
                    <p className="text-xs text-amber-400/80 mt-1.5">
                      These positions will be liquidated at market.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground/60">No open positions.</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant={mode === 'archive' ? 'warning' : 'danger'}
            onClick={handleConfirm}
            loading={deleting}
            disabled={loading || deleting || !confirmValid}
          >
            {mode === 'archive' ? 'Archive' : 'Delete'} Strategy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteStrategyModal;
